import { Component, DoCheck, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { AbstractControl, FormGroup, NgForm } from "@angular/forms";
import { ActivatedRoute, MaybeAsync, NavigationExtras, ParamMap, Params, Router } from "@angular/router";

import { IMenuOption, IPaginatorChangeEventData } from "@tylertech/forge";

import { Command } from "@tylertech/commands-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import {
    CachedRepositoryService,
    ErrorInfo,
    FeatureFlagHelperService,
    FormMetadataService,
    KeyHandlerService,
    NavigationService,
    NavMenuService,
    SecurityService,
    StorageService
} from "@tylertech/core-module";
import { IShellService } from "@tylertech/incode-module";
import { getLoggerFor, ILogger, LogAs, LogInvocation, LogLevel, LogName } from "@tylertech/logging";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { StringHelper, Utilities } from "@tylertech/shared-module";
import { AfterSaveContext, ICombinedViewMetadata, IFormMetadata, IHeaderBadge, IMetadataAction, IViewMetadata, Key, KeyValuePair, ModifierKey, PropertyMetadata, RecalculateProperty, SaveHandlingType } from "@tylertech/tyler-api-types";
import { AugmentedModelObject, EntityWrapper, LazyLoadedProxyHandler, ModifierCommandArgs, RepositoryFormData, goToFormBoundMethodParameter, onDeleteBoundMethodParameter, onDeleteBoundMethodSignature, onDeleteMethodSignature, } from "../../interfaces";
import { AfterMetadataFormSave, BreadcrumbArg, FormTitleService, GridDeleteService, GridEventService, MetadataActionService, MetadataEventService, MetadataFormService, MetadataIframeService, MetadataInputSelectService, MetadataLoadViewService, MetadataPropertyService, MetadataService, MetadataViewService, MultipleEntityEditService, } from "../../services";
import { ProviderStorageService } from "../../services/table/provider.storage.service";
import { ArrayHelpers, FormState, MetadataActionHelpers, MetadataFormStack, ObjectHelpers } from "../../utilities";
import { IntentService } from "@tylertech/intents-module";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject, takeUntil } from "rxjs";

export interface MetadataFormOnReturnParameter
{
    cancelled: boolean;
    dataController: RepositoryFormData;
    newForm: boolean;
}
export interface FormNavigateArgs
{
    form: IFormMetadata;
}
// @LogComponentChanges
@Component({
    selector: "metadata-form",
    templateUrl: "./metadata.form.tmpl.html",
    standalone: false
})
export class MetadataFormComponent implements OnInit, DoCheck, OnDestroy,
    // @see https://www.typescriptlang.org/docs/handbook/utility-types.html#recordkt
    Record<"onDelete", onDeleteMethodSignature>
{
    private readonly log: ILogger;

    @Input() formTitle: string;
    // TODO - what would we expect metadata-form to do if the parent component rebinds `form`, `model`, or `dataController` (or really any of our bindingFields)?
    @Input() readonly form: IFormMetadata;
    @Input() model: AugmentedModelObject;
    @Input() datacontroller: RepositoryFormData;
    @Input() returnBindingIsSet: boolean = false;
    @Output() onReturn = new EventEmitter<MetadataFormOnReturnParameter>();
    @Input() returnDataOnSave: boolean;
    @Input() newForm: boolean;
    @Input() passback: string;
    @Input() navigateBackOnReturn: boolean;
    @Input() closeOnFinish: boolean;
    @Input() viewOnly: boolean;
    @Output() modifierCallback = new EventEmitter<ModifierCommandArgs>();
    @Output() formNavigate: (params: FormNavigateArgs) => void;
    @Input() saveLabelText: string;
    @Input() saveMessage: string;
    @Input() initialView: string;
    @Input() hideHeaderCheckbox: boolean;
    @Input() hideHeaderSelections: boolean;
    @Input() createNewModelOnLoad: boolean;

    @Input()
    set customViewCommands(value: ReadonlyArray<Command>) //Passthrough
    {
        this._viewCommands = value;
    }

    @ViewChild("formCtrl") formCtrl: NgForm;

    private readonly queryParams = new Map<string, string>();
    private formStack: MetadataFormStack<AugmentedModelObject, RepositoryFormData>;

    saving: boolean = false;
    resaving: boolean = false;
    canceling: boolean = false;

    private formSaved: boolean = false;

    private directSave: boolean = false;
    private projectionEdit: boolean = false;

    private loading: boolean = false;
    refreshing: boolean = false;
    navigating: boolean = false;

    private resolveFailed: boolean = false;

    recalculating: boolean = false;

    private navigateEvent: IPaginatorChangeEventData;

    public isStackDirty: boolean = false;

    private listenerId: string;
    private _viewCommands: ReadonlyArray<Command>;

    private navigateBackUrl: string;

    private readonly saveText: string = "save";
    private readonly newText: string = "saveAndNew";
    private readonly copyText: string = "saveAndCopy";
    compactMode: boolean = false;

    isSelectedView(view: IViewMetadata): boolean
    {
        return this.selectedView === view;
    }

    /**
     * ! isStepper
     */
    saveEarlyOptions: IMenuOption[] = [];

    busyMessage: string = "";
    private defaultBusyMessage = "Loading...";

    constructor(
        private readonly $featureFlagHelperService: FeatureFlagHelperService,
        private readonly $commonDialogsService: CommonDialogsService,
        private readonly $storageService: StorageService,
        private readonly $odataFilterFactoryService: ODataFilterFactoryService,
        private readonly $shellService: IShellService,
        private readonly $intentService: IntentService,
        private readonly $navMenuService: NavMenuService,
        private readonly $navigationService: NavigationService,
        private readonly $repositoryService: CachedRepositoryService,
        private readonly $formMetadataService: FormMetadataService,
        private readonly $metadataService: MetadataService,
        private readonly $metadataFormService: MetadataFormService,
        private readonly $metadataViewService: MetadataViewService,
        private readonly $metadataActionService: MetadataActionService,
        private readonly $metadataPropertyService: MetadataPropertyService,
        private readonly $metadataEventService: MetadataEventService,
        private readonly $gridEventService: GridEventService,
        private readonly $metadataInputSelectService: MetadataInputSelectService,
        private readonly $multipleEditEntityService: MultipleEntityEditService,
        private readonly $gridDeleteService: GridDeleteService,
        private readonly $formTitleService: FormTitleService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly $metadataIframeService: MetadataIframeService,
        private readonly $providerStorageService: ProviderStorageService,
        private readonly $metadataLoadViewService: MetadataLoadViewService,
        private readonly $keyHandlerService: KeyHandlerService,
        private readonly $securityService: SecurityService
    )
    {
        this.log = getLoggerFor("MetadataFormComponent",
            () =>
            {
                return this.currentForm
                    ? `MetadataFormComponent("${this.currentForm.Name}")`
                    : "MetadataFormComponent";
            });

        activatedRoute.queryParamMap
            .pipe(takeUntilDestroyed())
            .subscribe((paramMap: ParamMap) =>
            {
                this.queryParams.clear();

                paramMap.keys.forEach(
                    (k) =>
                    {
                        this.queryParams.set(k, paramMap.get(k));

                    });
                this.handleBrowserNavigate();
            });

        this.listenerId = Utilities.generateRandomGuid();
        this.$metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                this.loading = args.ShowLoading && args.Recalculating;
                this.recalculating = args.Recalculating;
                this.busyMessage = args.Message ?? this.defaultBusyMessage;
            });
        this.$metadataEventService.onGoToForm.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                this.navigateFormBind(args);
            });
        this.$metadataEventService.onMetadataRefresh.pipe(takeUntilDestroyed())
            .subscribe(() =>
            {
                this.loading = true;
                this.lazyLoadForm();
            });
        this.$gridEventService.onGridEditViewLoading.pipe(takeUntilDestroyed())
            .subscribe(loading =>
            {
                this.loading = loading;
            });
        this.$metadataEventService.onNavigateView.pipe(takeUntilDestroyed())
            .subscribe(viewTitle =>
            {
                this.select(this.getViews().find(x =>
                {
                    const fullView = viewTitle.indexOf("/") > -1 && x.Path ? `${x.Path}/${x.Title}` : x.Title;
                    return fullView === viewTitle;
                }));
            });

        this.$metadataEventService.onSaveAfterRecalculate.pipe(takeUntilDestroyed())
            .subscribe(() =>
            {
                if (this.currentModel?.$saveAfterRecalculate)
                {
                    const overrideAction = this.currentModel.$saveAfterRecalculateAction;
                    delete this.currentModel.$saveAfterRecalculate;
                    delete this.currentModel.$saveAfterRecalculateAction;
                    this.save(overrideAction);
                }
                else if (this.currentModel?.$paginatorChangeEvent)
                {
                    const paginatorChangeEvent = this.currentModel.$paginatorChangeEvent;
                    delete this.currentModel.$paginatorChangeEvent;
                    this.paginatorChangeImpl(paginatorChangeEvent);
                }
            });
    }

    @HostListener("window:beforeunload", ["$event"])
    onWindowBeforeUnload($event: BeforeUnloadEvent): void
    {
        if (this.suppressLeaveSitePrompt($event?.target))
        {
            return;
        }

        if (this.maybeDirty(false))
        {
            $event.preventDefault();
            $event.returnValue = "";
        }
    }

    suppressLeaveSitePrompt(target: any): boolean
    {
        return !!window["webLauncher"] && target?.activeElement?.tagName !== "BUTTON";
    }

    /**
     * Determines if there is any unsaved data and asks the user if they want to continue with a navigation away, or
     * cancel the navigation and remain.
     * @returns `true` if there is no unsaved data, `Promise<boolean>` otherwise.
     */
    canDeactivateRoute(): MaybeAsync<boolean>
    {
        return this.isDirtyPrompt(false);
    }

    isDirtyPrompt(fromCancel: boolean): Promise<boolean>
    {
        if (this.maybeDirty(fromCancel))
        {
            // TODO(DO-4549) - Product may want a different message.
            return this.$commonDialogsService.showConfirmationMessage(
                "There is unsaved data that will be lost if you leave this page. Continue?",
                null,
                "Yes",
                "No")
                .then(
                    () => true,
                    () => false);
        }
        return Promise.resolve(true);
    }

    maybeDirty(fromCancel: boolean): boolean
    {
        if (!this.saving
            && (fromCancel || !this.canceling)
            && !this.$metadataFormService.cantSave(this.currentForm)
            && !this.formSaved
            && !this.hideActions
            && (this.hasChanges || this.isStackDirty)
            && !this.$metadataActionService.actionRedirecting)
        {
            return true;
        }
        return false;
    }

    handleBrowserNavigate(): void
    {
        if (!this.browserNavigating)
            return;
        this.browserNavigating = false;

        const newViewTitle = this.queryParams.get("view");
        const newView = this.getViews().find(x =>
        {
            const path = x.Path ? `${x.Path}/` : "";
            return `${path}${x.Title}` === newViewTitle;
        });

        //Back to sibling view on same form
        if (newViewTitle && newView)
            this.select(newView);
        //Back to grid if i am on edit and havent changed views within edit form
        else if (!this.isInitialForm)
        {
            if (this.canCancel)
                this.cancel();
            else
                window.history.forward(); // Block back navigation when cancel isnt allowed
        }
        //Else do nothing, normal routing will take over
    }

    browserNavigating: boolean = false;
    @HostListener("window:popstate", ["$event"])
    onWindowPopstate($event: PopStateEvent): void
    {
        $event.preventDefault();
        this.browserNavigating = true;
    }

    isFieldInView(field: string, view: IViewMetadata): boolean
    {
        const self = this;
        if (this.$metadataViewService.IsCombinedView(view))
        {
            let inView = false;
            view.Views.forEach(v =>
            {
                if (!inView)
                    inView = self.isFieldInView(field, v);
            });
            return inView;
        }

        if (!view.ViewMetaData)
            return false;
        return view.ViewMetaData.Properties?.filter(p => p.Name === field).length > 0;
    }

    currentUrl: string;

    modifierCallbackBind(arg: ModifierCommandArgs): void
    {
        this.modifierCallback.emit(arg);
    }

    get initialViewSet(): boolean
    {
        return this.formStack.InitialViewSet;
    }

    set initialViewSet(value: boolean)
    {
        this.formStack.InitialViewSet = value;
    }

    select(selectedView: IViewMetadata): void
    {
        const leavingViewIsInForm = this.leavingView && this.getViews().indexOf(this.leavingView) > -1;
        if (leavingViewIsInForm)
            selectedView = this.leavingView;

        if (this.currentState.selectedView !== selectedView || !this.initialViewSet)
        {
            this.currentState.selectedView = selectedView;
            if (this.currentForm.Views.length > 1)
            {
                const path = selectedView.Path ? `${selectedView.Path}/` : "";
                this.updateQueryParams({ "view": `${path}${selectedView.Title}` });
            }
            else if (this.currentForm.Views.length === 1 && !this.isInitialForm)
                this.updateQueryParams({ "view": "Default" });

            this.initialViewSet = true;
        }
    }

    private lazyLoadForm(): void
    {
        const self = this;
        const lazyLoadFormType = this.currentForm["$LazyLoadFormType"];
        if (lazyLoadFormType)
        {
            let filter = null;
            if (this.currentModel)
            {
                const constantFilter = this.$metadataService.EvaluateString(
                    this.currentForm["$LazyLoadFilter"],
                    this.currentModel.$$Parent,
                    this.currentModel,
                    this.currentDataController, true
                );
                filter = this.$odataFilterFactoryService
                    .createBuilderFor<IFormMetadata>()
                    .constantFilter(constantFilter)
                    .build();
            }
            this.loading = true;
            this.$formMetadataService.GetByType(lazyLoadFormType, filter)
                .then(r =>
                {
                    self.$metadataViewService.UpdateViewMetadata(self.currentForm.Views, r.Views);
                    self.$metadataService.FireGridReload(self.currentDataController.Type, true, null, true);
                })
                .finally(() =>
                {
                    self.refreshing = true;
                    if (self.selectedView.OnShown)
                        self.selectedView["$lazyLoadComplete"] = true;
                    requestAnimationFrame(()=> self.refreshing = false);
                    self.loading = false;
                });
        }
    }

    ngOnInit(): void
    {
        const initialForm = this.form;
        let self = this;

        if (this.navigateBackOnReturn)
            this.navigateBackUrl = document.referrer;

        if (initialForm instanceof MetadataFormStack)
        {
            self.formStack = initialForm;
            return;
        }
        if (self.$metadataFormService.isGridForm(initialForm))
        {
            // if this is a grid form then we shouldn't have a primary type or model!
            if (self.model)
            {
                console.error(
                    JSON.stringify(
                        {
                            component: MetadataFormComponent.name,
                            form: `${initialForm.Path}/${initialForm.Name}`,
                            message: "'Grid'-type IFormMetadata's should not be initialized with a model.",
                        },
                        null,
                        4
                    )
                );
            }
            initialForm.Views[0]["$fixedHeight"] = true;
            // do not override existing data controllers
            const datacontroller = self.datacontroller || new RepositoryFormData(null, null);
            self.initializeFormStack(initialForm, self.model, datacontroller);
            return;
        }
        self.resolveModelAndCollection(initialForm, self.model, self.datacontroller)
            .then(
                x =>
                {
                    const initialView = initialForm.Views.find(x => x.InitialView);
                    if (initialView)
                        this.updateQueryParams({ "view": initialView.Title });

                    self.initializeFormStack(initialForm, x.model, x.dataController);
                    if (!self.datacontroller) self.datacontroller = self.currentDataController;
                    if (!self.model) self.model = self.currentModel;
                    self.currentForm.Views.forEach(v => v["$fixedHeight"] = self.$metadataViewService.IsGrid(v));
                    self.runOnShown(initialForm, x.model, x.dataController);
                    if (self.currentForm?.HeaderBadges)
                    {
                        self.headerBadges = [];
                        self.currentForm?.HeaderBadges.forEach(badge =>
                        {
                            const text = self.$metadataService.EvaluateString(
                                badge.Text, null, self.model
                            );

                            self.headerBadges.push({
                                Theme: badge.Theme,
                                Text: text,
                                HiddenProperty: badge.HiddenProperty
                            });
                        });
                    }
                    else
                    {
                        self.headerBadges = null;
                    }
                },
                _e =>
                {
                    self.resolveFailed = true;
                }
            );
        if (self.$metadataViewService.IsRecalculableForm(initialForm))
        {
            initialForm["$LazyLoadFilter"] = initialForm.PropertyMetadataFilter;
            initialForm["$LazyLoadFormType"] = initialForm.RecalculableFormType;
        }
        self.$keyHandlerService.subscribe(self.listenerId, Key.Home, ModifierKey.Ctrl, "This Screen|Set focus on first input", () =>
        {
            this.$metadataViewService.focusFirstMetadataInput(this.selectedView);
        });

        self.$keyHandlerService.subscribe(self.listenerId, Key.S, ModifierKey.Alt, "This Screen|Save", () =>
        {
            //TODO: MJ - When we move to Forge3, need to change how we detect that a dialog is open, mat-dialog-container selector wont be good anymore
            if (self.loaded && self.canSave() && !self.hasActionOverrides() && (!document.querySelectorAll(".mat-dialog-container") || document.querySelectorAll(".mat-dialog-container").length === 0))
                self.save();
        });

        self.$keyHandlerService.subscribe(self.listenerId, Key.ArrowRight, ModifierKey.Shift, "Stepper|Advance to next step", () =>
        {
            if (self.IsStepper && self.StepControlsEnabled())
            {
                if (!self.LastStep())
                {
                    self.next();
                }
                else
                {
                    if (self.LastStep() && !self.isViewOnly)
                    {
                        self.saveAction(false);
                    }
                }
            }
        });

        self.$keyHandlerService.subscribe(self.listenerId, Key.ArrowLeft, ModifierKey.Shift, "Stepper|Return to previous step", () =>
        {
            if (self.IsStepper && !self.SelectedViewCellEditing() && self.selectedIndex !== 0)
            {
                self.previous();
            }
        });

        this.compactMode = this.$securityService.IsCompactMode;

        if (Utilities.IsNullOrUndefinedOrEmpty(this.busyMessage))
        {
            this.busyMessage = this.defaultBusyMessage;
        }
    }

    ngOnDestroy(): void
    {
        this.deInitializeBreadCrumb(true);
        this.$keyHandlerService.unsubscribeAllFromClass(this.listenerId);
        this.$metadataInputSelectService.cleanup();
        this.$repositoryService.ClearAll();
        this.datacontroller?.ClearAll();
    }

    ngDoCheck(): void
    {
        if (this.formStack && this.currentModel)
        {
            if (this.currentForm?.TitleProperty)
                this.$formTitleService.setTitle(this.$metadataService.DependentModuleValue(
                    this.currentForm.TitleProperty,
                    this.currentModel?.$$Parent,
                    this.currentModel
                ));

            if (this.currentForm?.SaveLabelProperty)
            {
                const label = this.saveLabel;
                if (this.saveButtonText?.indexOf(label) === -1)
                    this.setSaveButton();
            }
        }
    }

    @HostListener("window:message", ["$event"])
    onWindowMessage($event: MessageEvent<KeyValuePair<string, any>>): void
    {
        if (!this.$metadataIframeService.insideIframe || !this.isEmbeddedByRequest)
        {
            return;
        }

        if ($event.data.Key === "metadataIframeCloseRequest")
        {
            this.doSave();
        }
    }

    private resolveModelAndCollection(
        formMetadata: IFormMetadata,
        model: AugmentedModelObject,
        dataController: RepositoryFormData
    ): Promise<{
        model: AugmentedModelObject,
        dataController: RepositoryFormData;
    }>
    {
        if (model)
        {
            return Promise.resolve({
                model: model,
                dataController: dataController || new RepositoryFormData(formMetadata.BoundType, model),
            });
        }
        const self = this;
        let filterString = this.$odataFilterFactoryService.getFilterFromLocation();
        let firstView = formMetadata.Views ? formMetadata.Views[0] : null;
        if (firstView && this.$metadataViewService.IsCombinedView(firstView))
            firstView = (<ICombinedViewMetadata>firstView).Views[0];
        if (
            firstView &&
            firstView.ViewMetaData &&
            firstView.ViewMetaData.TypeName === formMetadata.BoundType &&
            firstView.IsRepositoryType === false
        )
        {
            return Promise.resolve(resolveModel({}));
        }
        if (formMetadata.ConstantFilter)
        {
            filterString = addOrCreateFilterString(filterString, formMetadata.ConstantFilter);
        }
        else if (firstView && firstView.ViewMetaData && firstView.ViewMetaData.TypeName === formMetadata.BoundType)
        {
            if (firstView.ConstantFilter)
            {
                filterString = addOrCreateFilterString(filterString, firstView.ConstantFilter);
            }
        }
        const getNewModel = JSON.stringify({
            ConfirmationQuestions: dataController ? dataController.ConfirmationQuestions : {},
        });
        const dataControllerForRejection = dataController ? dataController : <RepositoryFormData>{ ConfirmationQuestions: {} };
        const rejectionHandler = this.$metadataActionService.GetVerificationHandler(
            dataControllerForRejection,
            () => self.resolveModelAndCollection(formMetadata, model, dataControllerForRejection),
            model
        );

        if (this.queryParams.has("editFormId"))
        {
            dataController = new RepositoryFormData(formMetadata.BoundType, model);
            const rowTypeName = formMetadata.BoundType;
            const idProperty = this.queryParams.get("idProperty");
            const id = this.queryParams.get("editFormId") ?? "Id";
            let filter = self.$odataFilterFactoryService.getFilterFromLocation();
            return dataController.findExistingOrGet(rowTypeName, idProperty, id, self.$repositoryService, dataControllerForRejection.ConfirmationQuestions, filter)
                .then(result =>
                {
                    result.$$TypeName = rowTypeName;
                    dataController = null;
                    return resolveModel(result);
                }, rejectionHandler);
        }

        if (this.createNewModelOnLoad)
        {
            return self.$repositoryService
                .GetNew(
                    {
                        Item1: formMetadata.BoundType,
                        Item2: getNewModel,
                    },
                    filterString
                )
                .then(resolveModel, rejectionHandler);
        }
        return this.$repositoryService
            .FetchWithQuestions({ Item1: formMetadata.BoundType, Item2: dataControllerForRejection.ConfirmationQuestions }, filterString)
            .then(getFirstOrNew, rejectionHandler);
        function getFirstOrNew(r: AugmentedModelObject[]): Promise<{
            model: AugmentedModelObject,
            dataController: RepositoryFormData;
        }>
        {
            if (!dataController || (!dataController.Entity && !dataController.OtherEntities))
                dataController = null;
            return r.length === 0
                ? self.$repositoryService.GetNew(
                    {
                        Item1: formMetadata.BoundType,
                        Item2: getNewModel,
                    },
                    filterString
                )
                    .then(x => resolveModel(x))
                : Promise.resolve(resolveModel(r[0]));
        }
        function resolveModel(resolvedModel: AugmentedModelObject): { model: any, dataController: RepositoryFormData; }
        {
            let resolvedController: RepositoryFormData = dataController;
            resolvedModel.$$TypeName = formMetadata.BoundType;
            const modelWrapper = new EntityWrapper(resolvedModel, self.$metadataService);
            const wrappedModel = new Proxy(modelWrapper, new LazyLoadedProxyHandler());
            if (!resolvedController)
            {
                resolvedController = new RepositoryFormData(formMetadata.BoundType, wrappedModel);
            }
            else
            {
                resolvedController.Add(formMetadata.BoundType, wrappedModel, filterString);
            }
            return {
                model: wrappedModel,
                dataController: resolvedController,
            };
        }
        function addOrCreateFilterString(currentFilter: string, additionalFilter: string): string
        {
            if (!Utilities.IsNullOrUndefinedOrEmpty(currentFilter)) return currentFilter + " and " + additionalFilter;
            else return self.$odataFilterFactoryService.createBuilder()
                .constantFilter(additionalFilter)
                .build();
        }
    }

    private initializeFormStack(
        formMetadata: IFormMetadata,
        model: AugmentedModelObject,
        dataCollection: RepositoryFormData
    ): void
    {
        this.formStack = new MetadataFormStack<AugmentedModelObject, RepositoryFormData>(
            formMetadata,
            model,
            dataCollection
        );

        if (this.$metadataViewService.IsSearchForm(this.currentForm))
        {
            this.populateBreadCrumbFilter();
            this.registerSearchBreadCrumbListener();
        }

        const viewParam = this.queryParams.get("view");
        if (viewParam)
        {
            const view = ArrayHelpers.first(formMetadata.Views, v => `${v.Path ? `${v.Path}/` : ""}${v.Title}` === viewParam);
            const hasViewParam = view && !this.IsStepper && !this.IsProcessingStep;
            const viewDisabled = this.$metadataViewService.IsDisabled(view, dataCollection?.Entity ?? model, dataCollection);
            if (!hasViewParam || viewDisabled)
            {
                this.updateQueryParams({ "view": null }, true);
                this.initialView = null;
            }
            else if (!this.initialView)
                this.initialView = this.queryParams.get("view");
        }

        if (!this.hideActions && this.isInitialForm)
        {
            this.currentForm["$separateEditForm"] = !Utilities.IsNullOrUndefined(this.queryParams.get("n"));
            this.setSaveButton();
        }

        const initialView = !Utilities.IsNullOrUndefined(this.initialView)
            ? ArrayHelpers.first(formMetadata.Views, v => `${v.Path ? `${v.Path}/` : ""}${v.Title}` === this.initialView)
            : null;
        const toSelect = Utilities.IsNullOrUndefined(initialView) ? this.getViews()[0] : initialView;
        this.select(toSelect);
    }

    get showHeaderBadge(): boolean
    {
        return !!this.visibleHeaderBadges && this.visibleHeaderBadges.length > 0;
    }

    private _headerBadges: IHeaderBadge[] = null;
    get headerBadges(): IHeaderBadge[]
    {
        return this._headerBadges;
    }

    set headerBadges(value: IHeaderBadge[])
    {
        this._headerBadges = value;
    }

    get visibleHeaderBadges(): IHeaderBadge[]
    {
        return this._headerBadges?.filter(
            x => !this.$metadataService.EvaluateBoolean(x.HiddenProperty, null, this.model)
        );
    }

    get loaded(): boolean
    {
        return !!this.formStack;
    }

    get SeparateEditForm(): boolean
    {
        return this.currentForm["$separateEditForm"];
    }

    private get currentState(): FormState<AugmentedModelObject, RepositoryFormData>
    {
        return this.formStack.current;
    }

    get currentForm(): IFormMetadata
    {
        return this.formStack?.form;
    }

    get currentModel(): AugmentedModelObject
    {
        return this.formStack?.model;
    }

    get currentDataController(): RepositoryFormData
    {
        return this.formStack?.dataCollection;
    }

    get selectedView(): IViewMetadata
    {
        return this.formStack?.current.selectedView;
    }

    get selectedIndex(): number
    {
        const views = this.getViews();
        if (this.isViewHidden(this.selectedView))
        {
            this.select(views[0]);
        }
        return views.indexOf(this.selectedView);
    }

    /** set_selectedIndex is used when isStepper */
    set selectedIndex(value: number)
    {
        const totalViews = this.currentForm.Views.length - 1;
        const safeIndex = value < 0 ? 0 : value >= totalViews ? totalViews : value;
        const views = this.getViews();
        this.select(views[safeIndex]);
    }

    private _isInitialForm: boolean = false;

    private get isInitialForm(): boolean
    {
        const ret = this.formStack.length === 1;
        if (this._isInitialForm !== (ret))
        {
            this._isInitialForm = ret;
            this.$metadataFormService.initialForm = ret;
        }

        return ret;
    }

    private get isInitialFormStepper(): boolean
    {
        return this.$metadataFormService.isInitialFormStepper(this.formStack);
    }

    get viewCommands(): ReadonlyArray<Command> { return this.isInitialForm ? this._viewCommands : undefined; }

    get formActions(): IMetadataAction[]
    {
        return this.currentForm.Actions;
    }

    get hasFormActions(): boolean
    {
        return !Utilities.IsNullOrUndefinedOrEmpty(this.currentForm.Actions);
    }

    get showTitleOnForm(): boolean
    {
        if (!this.selectedView)
        {
            return false;
        }

        if (this.$metadataViewService.IsCombinedView(this.selectedView))
        {
            return this.hasFormActions;
        }
        else if (this.$metadataViewService.IsGrid(this.selectedView))
        {
            return true;
        }
        else
        {
            return this.hasFormActions &&
                !this.$metadataViewService.IsCard(this.selectedView) &&
                !this.selectedView.IsDocumentView;
        }
    }

    get shouldShowNav(): boolean
    {
        return !this.IsStepper && this.currentForm.Views.length > 1;
    }

    get flexAmount(): number
    {
        return this.shouldShowNav ? 80 : 100;
    }

    /**
     * @private AVAILABLE IN TEMPLATE
     * @returns `true` if we are on the initial form and the form is a gridForm or set to CantSave, `false` otherwise.
     */
    get hideActions(): boolean
    {
        return this.$metadataIframeService.insideIframe
            || this.isEmbeddedByRequest
            || this.$metadataFormService.isAdminForm(this.formStack)
            || (this.isInitialForm && this.$metadataFormService.cantSave(this.currentForm));
    }

    get IsStepper(): boolean
    {
        return this.$metadataViewService.IsStepper(this.currentForm);
    }

    get IsProcessingStep(): boolean
    {
        return window.location.pathname.indexOf("/apps/processing/step") > -1;
    }

    get canCancel(): boolean
    {
        if (this.formStack.current.addMode) return true;
        const previousForm = this.formStack.previousState;
        if (previousForm)
        {
            const selectedView = ArrayHelpers.first(
                previousForm.formMetadata.Views,
                v => v.Title === previousForm.selectedView.Title
            );
            if (this.$metadataViewService.IsGrid(selectedView) && selectedView.DisableCancelOnEdit)
                return false;
            else
                return true;
        }
        return this.allowSave();
    }

    get saveLabel(): string
    {
        let saveLabel = "Save";
        if (!Utilities.IsNullOrUndefinedOrEmpty(this.saveLabelText))
            saveLabel = this.saveLabelText;
        else if (!Utilities.IsNullOrUndefinedOrEmpty(this.currentForm.SaveLabelText))
            saveLabel = this.currentForm.SaveLabelText;
        else if (!Utilities.IsNullOrUndefinedOrEmpty(this.currentForm.SaveLabelProperty))
            saveLabel = this.getModelValue(this.currentModel, this.currentForm.SaveLabelProperty);
        return this.allowSave() ? saveLabel : "Done";
    }

    getModelValue(model: object, propertyName: string): string
    {
        return this.$metadataService.DependentModuleValue(
            propertyName,
            model["$$Parent"],
            model
        );
    }

    get cancelLabel(): string
    {
        if (this.$metadataFormService.hasCustomCancelAction(this.form))
        {
            let customLabel = this.$metadataFormService.getCustomCancelLabel(this.form);
            if (!Utilities.IsNullOrUndefinedOrEmpty(customLabel))
            {
                return customLabel;
            }
        }
        if (!Utilities.IsNullOrUndefinedOrEmpty(this.currentForm.CancelLabelText))
            return this.currentForm.CancelLabelText;
        return "Cancel";
    }

    get isViewOnly(): boolean
    {
        return (
            this.viewOnly ||
            this.$metadataFormService.isFormViewOnly(this.currentForm, this.currentDataController) ||
            this.$metadataFormService.isFormViewOnly(this.form, this.currentDataController)
        );
    }

    stepperSelectionChange(event: any): void
    {
        const self = this;
        const newIndex = event.detail;
        if (newIndex > self.selectedIndex)
            self.navigateStepper(self.selectedView.OnNext, () => (self.selectedIndex = newIndex));
        else if (newIndex < self.selectedIndex)
            self.navigateStepper(self.selectedView.OnPrevious, () => (self.selectedIndex = newIndex));
    }

    navigateFormBind(arg: goToFormBoundMethodParameter)
    {
        this.navigateForm(arg.view, arg.model, arg.datacontroller, arg.addMode, arg.index, arg.directSave, arg.projectionEdit, arg.initialView);
    }

    navigateForm(
        formMetadata: IFormMetadata,
        model: AugmentedModelObject,
        datacontroller: RepositoryFormData,
        addMode: boolean = false,
        _index?: number,
        directSave?: boolean,
        projectionEdit?: boolean,
        initialView?: string
    ): void
    {
        // this.editIndex = index;
        this.navigating = true;
        this.directSave = directSave;
        this.projectionEdit = projectionEdit;
        this.viewOnly = formMetadata.ViewOnly || this.viewOnly;
        this.initialViewSet = false;
        this.deInitializeBreadCrumb(false);

        if (this.currentForm["$ChildFormSaveOverride"])
            formMetadata["$ChildFormSaveOverride"] = this.currentForm["$ChildFormSaveOverride"];

        this.pushNewState(formMetadata, model, datacontroller, addMode);
        if (initialView)
        {
            this.initialView = initialView;
            this.currentState.selectedView = this.getViews().find(x =>
            {
                return `${x.Path}/${x.Title}` === this.initialView
                    || x.Title === this.initialView;
            });
        }
        else if (this.getViews().length === 1)
            this.select(this.getViews()[0]);

        this.setSaveButton();
        requestAnimationFrame(() =>
        {
            this.navigating = false;
            this.leavingView = null;
        });
    }

    setSaveButton(): void
    {
        const saveLabel = this.saveLabel;
        let saveValue = this.saveText;

        if (this.showAddAnother)
        {
            let saveOption = {
                value: this.saveText,
                label: saveLabel
            };

            let newOption = {
                value: this.newText,
                label: `${saveLabel} and new`
            };

            let copyOption = {
                value: this.copyText,
                label: `${saveLabel} and copy`
            };

            if (this.formStack.AddNew)
            {
                this.saveOptions = [
                    saveOption,
                ];
                if (this.canCopy)
                {
                    this.saveOptions.push(copyOption);
                }
                this.saveButtonText = newOption.label;
                saveValue = newOption.value;
            }
            else if (this.canCopy && this.formStack.AddNewAndCopy)
            {
                this.saveOptions = [
                    saveOption,
                    newOption
                ];
                this.saveButtonText = copyOption.label;
                saveValue = copyOption.value;
            }
            else
            {
                this.saveOptions = [
                    newOption,
                ];
                if (this.canCopy)
                {
                    this.saveOptions.push(copyOption);
                }
                this.saveButtonText = saveOption.label;
                saveValue = saveOption.value;
            }

            this.saveEarlyOptions = this.saveOptions.concat([
                {
                    value: saveValue,
                    label: this.saveButtonText,
                }
            ]);
        }
        else
        {
            this.saveOptions = [];
            this.saveButtonText = saveLabel;
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    private updateQueryParams(
        @LogAs(x => JSON.stringify(x)) queryParams: Params,
        @LogName("replace") replace?: boolean): void
    {
        if (this.$navigationService.isNavigating || this.$navigationService.isPopStateNavigation)
            return;
        const extras: NavigationExtras = {
            relativeTo: this.activatedRoute,
            queryParams,
            queryParamsHandling: "merge",
            replaceUrl: replace,
            // skipLocationChange: !!queryParams.view,
            // onSameUrlNavigation: "reload"
        };
        this.router.navigate([], extras)
            .then(
                navigated =>
                {
                    this.log.debug(`Updating query parameters complete!`, { navigated, navConfig: { extras } });
                },
                error =>
                {
                    this.log.error(`Updating query parameters errored!`, { error, navConfig: { extras } });
                });
    }

    populateBreadCrumbFilter(): void
    {
        if (this.currentForm["$BreadcrumbFilter"])
        {
            this.updateQueryParams({ "$filter": this.currentForm["$BreadcrumbFilter"] });
            delete this.currentForm["$BreadcrumbFilter"];
        }
    }

    registerSearchBreadCrumbListener(): void
    {
        const self = this;
        this.$metadataEventService.onSearchFormBreadcrumb.pipe(takeUntil(this.breadCrumbSubject$))
            .subscribe((arg: BreadcrumbArg) =>
            {
                if (!self.$metadataViewService.IsSearchForm(self.currentForm))
                    return;
                if (self.currentForm.Views.filter(v => self.isFieldInView(arg.Field, v)).length === 0 && arg.Field !== "$Clear")
                    return;
                if (arg.Field === "$Clear")
                {
                    self.updateQueryParams({ "$filter": null });
                    return;
                }
                if (self.currentForm.BreadCrumbUpdateableFields != null && self.currentForm.BreadCrumbUpdateableFields.filter(v=>v === arg.Field).length === 0)
                    return;
                const filter = this.$odataFilterFactoryService.parseOdataFilterFromLocation();
                if (Utilities.IsNullOrUndefinedOrEmpty(arg.Value))
                    delete filter[arg.Field];
                else
                    filter[arg.Field] = arg.Value;
                if (Object.keys(filter).length === 0)
                    this.updateQueryParams({ "$filter": null });
                else
                {
                    const builder = this.$odataFilterFactoryService.createBuilder();
                    Object.keys(filter).forEach(key => builder.filterEQ(key, filter[key]));
                    this.updateQueryParams({ "$filter": builder.build().replace("&$filter=", "") });
                }
            });
    }

    private breadCrumbSubject$ = new Subject<void>();

    deInitializeBreadCrumb(updateQueryParam: boolean): void
    {
        if (updateQueryParam && this.$metadataViewService.IsSearchForm(this.currentForm))
        {
            this.currentForm["$BreadcrumbFilter"] = this.queryParams.get("$filter");
            if (!this.isInitialForm || !this.canceling)
                this.updateQueryParams({ "$filter": null });
        }
        this.breadCrumbSubject$.next();
        this.breadCrumbSubject$.complete();
        this.breadCrumbSubject$ = new Subject<void>();
    }

    private pushNewState(
        formMetadata: IFormMetadata,
        model: AugmentedModelObject,
        dataController: RepositoryFormData,
        addMode: boolean,
        selectedView?: IViewMetadata
    ): void
    {
        this.pushNewStateImpl(formMetadata, model, dataController, addMode, selectedView);
    }

    private pushNewStateImpl(formMetadata: IFormMetadata, model: AugmentedModelObject, dataController: RepositoryFormData, addMode: boolean, selectedView?: IViewMetadata): void
    {
        let queryParams = <Params>(dataController["$editFormQueryParams"] ?? {});
        if (this.$metadataViewService.IsSearchForm(this.currentForm))
        {
            this.currentForm["$BreadcrumbFilter"] = this.queryParams.get("$filter");
            queryParams.$filter = null;
        }
        this.$metadataFormService.pushNewState(this.formStack, formMetadata, model, dataController, addMode, this.hasChanges, selectedView);
        if (queryParams)
        {
            this.updateQueryParams(queryParams);
            delete dataController["$editFormQueryParams"];
        }
        this.isStackDirty = false;
        this.markPristine(this.formCtrl.controls);
        this.scrollViewToTop();
        if (!Utilities.IsNullOrUndefined(this.formNavigate))
        {
            this.formNavigate({ form: formMetadata });
        }
        this.$metadataViewService.AllBaseLevelViews(formMetadata).forEach(v => delete v["$HasBeenLoaded"]);
        this.runOnShown(formMetadata, model, dataController);
    }

    private runOnShown(
        formMetadata: IFormMetadata,
        model: AugmentedModelObject,
        dataController: RepositoryFormData
    ): Promise<AfterSaveContext>
    {
        if (formMetadata.OnShown)
        {
            const PropertyMetadata = <PropertyMetadata>{
                Name: formMetadata.OnShown.PropertyToChange,
                RecalculateOnChange: this.$metadataPropertyService.getPropertiesToRecalculate(formMetadata.OnShown),
                RecalculateWithParent: formMetadata.OnShown.RecalculateWithParent,
                RecalculateToIgnore: formMetadata.OnShown.PropertiesToIgnore
            };
            // TODO - you do know that `model.$$TypeName` is a string and `dataController` is an object right?
            const typeName = model["$$TypeName"] ? model["$$TypeName"] : dataController;
            dataController["$setInitialModCounter"] = Utilities.IsNullOrUndefined(dataController["$setInitialModCounter"]) ? 1 : dataController["$setInitialModCounter"] + 1;
            return this.$metadataPropertyService.recalculateProperties(
                typeName,
                PropertyMetadata,
                model,
                model["$$Parent"],
                model[formMetadata.OnShown.PropertyToChange],
                undefined,
                false,
                false,
                dataController
            );
        }
        return Promise.resolve(<AfterSaveContext>{});
    }

    get showLoading(): boolean
    {
        return ((!this.loaded || this.loading || this.saving || this.navigating) && !this.resolveFailed) && this.$metadataActionService.activeDialogIndex === 0;
    }

    get title(): string
    {
        return !this.formTitle ? this.selectedView.Title : this.formTitle + " - " + this.selectedView.Title;
    }

    getViews(): IViewMetadata[]
    {
        return this.currentForm.Views.filter(x => !this.isViewHidden(x));
    }

    getAllViews(): IViewMetadata[]
    {
        return this.currentForm.Views;
    }

    getIndex(step: IViewMetadata): number
    {
        return this.getViews().indexOf(step);
    }

    getFooterView(): IViewMetadata
    {
        let footer: IViewMetadata = null;
        if (this.hasFooterView() && !this.isViewHidden(this.currentForm.FooterView))
            footer = this.currentForm.FooterView;
        return footer;
    }

    isViewHidden(view: IViewMetadata): boolean
    {
        return this.$metadataViewService.IsHidden(view, this.currentModel, this.currentDataController);
    }

    /**
     * ! isStepper
     */
    getNextRequiredStep(): IViewMetadata
    {
        const views = this.getViews();
        if (this.selectedIndex >= views.length - 2) return null; // This is the last or penultimate step
        let i = 1;
        if (!this.isOptional(views[this.selectedIndex + i])) return null; // The next step is required
        while (this.isOptional(views[this.selectedIndex + i]))
        {
            i++; // Find the next non-optional view
        }
        return views[Math.min(this.selectedIndex + i, views.length - 1)];
    }

    /**
     * ! isStepper
     */
    private isOptional(step: IViewMetadata): boolean
    {
        return (
            this.$metadataViewService.IsStepper(this.currentForm) &&
            this.currentForm.OptionalViews.indexOf(step.Title) >= 0
        );
    }

    /**
     * ! isStepper
     */
    LastStep(): boolean
    {
        return this.selectedIndex === this.getViews().length - 1;
    }

    /**
     * ! isStepper
     */
    SelectedViewCellEditing(): boolean
    {
        return this.$metadataViewService.IsViewCellEditing(this.selectedView);
    }

    /**
     * ! isStepper
     */
    remainingStepsOptional(): boolean
    {
        let ret = false;
        if (Utilities.IsNullOrUndefinedOrEmpty(this.saveEarlyOptions) && !this.hasActionOverrides())
            return ret;
        if (this.$metadataViewService.IsStepper(this.currentForm) && this.currentForm.OptionalViews?.length > 0)
        {
            const views = this.getViews();
            const selectedIndex = this.selectedIndex;

            if (selectedIndex < views.length - 1)
            {
                for (let i = this.selectedIndex + 1, l = views.length; i < l; i++)
                {
                    if (!this.isOptional(views[i]))
                    {
                        ret = false;
                        break;
                    }
                    else
                    {
                        ret = true;
                    }
                }
            }
        }

        return ret;
    }

    StepControlsEnabled(): boolean
    {
        return this.StepIsValid() && !this.SelectedViewCellEditing();
    }

    StepIsValid(): boolean
    {
        if (!this.IsStepper)
        {
            return !this.formCtrl.invalid;
        }
        if (!this.formCtrl)
            return true;
        const form = this.formCtrl.controls[this.getViews()[this.selectedIndex].Title];
        return Utilities.IsNullOrUndefined(form) ? true : !form.invalid;
    }

    StepIsCompleted(index: number): boolean
    {
        return index < this.selectedIndex;
    }

    StepIsSelected(index: number): boolean
    {
        return index === this.selectedIndex;
    }

    StepDisabled(step: IViewMetadata): boolean
    {
        if (this.SelectedViewCellEditing())
            return true;
        try
        {
            const views = this.getViews();
            const viewIndex = views.indexOf(step);
            if (viewIndex === this.selectedIndex)
                return false;

            if (viewIndex < this.selectedIndex) {
                if (viewIndex === this.selectedIndex - 1)
                    return false;
                for (let i = viewIndex + 1; i < this.selectedIndex; i++)
                    if (!this.isOptional(views[i]))
                        return true;
                return false;
            }
            if (viewIndex > this.selectedIndex) {
                if (viewIndex === this.selectedIndex + 1)
                    return false;
                for (let i = this.selectedIndex + 1; i < viewIndex; i++)
                    if (!this.isOptional(views[i]))
                        return true;
                return false;
            }
        }
        catch {
            return true;
        }
        return true;
    }

    /**
     * ! isStepper
     */
    next(): void
    {
        const self = this;
        self.navigateStepper(self.selectedView.OnNext, () =>
        {
            self.selectedIndex++;
        });
    }

    /**
     * ! isStepper
     */
    previous(): void
    {
        const self = this;
        self.navigateStepper(self.selectedView.OnPrevious, () =>
        {
            self.selectedIndex--;
        });
    }

    navigateStepper(recalcProperty: RecalculateProperty, navigationFunction: () => any): Promise<void>
    {
        this.isStackDirty = this.formCtrl.dirty || this.isStackDirty;
        if (!Utilities.IsNullOrUndefined(recalcProperty))
        {
            const self = this;
            const PropertyMetadata = <PropertyMetadata>{
                Name: recalcProperty.PropertyToChange,
                RecalculateOnChange: self.$metadataPropertyService.getPropertiesToRecalculate(recalcProperty),
                RecalculateWithParent: recalcProperty.RecalculateWithParent,
            };
            let model = this.currentModel;
            if (
                self.selectedView.SubPropertyType &&
                self.currentDataController.OtherEntities[self.selectedView.SubPropertyType]
            )
                model = this.currentDataController.OtherEntities[self.selectedView.SubPropertyType][0];
            const typeName = model["$$TypeName"] ? model["$$TypeName"] : this.currentDataController.Type;
            return this.$metadataPropertyService
                .recalculateProperties(
                    typeName,
                    PropertyMetadata,
                    model,
                    model["$$Parent"],
                    model[recalcProperty.PropertyToChange],
                    undefined,
                    true,
                    true,
                    this.currentDataController
                )
                .then(r =>
                {
                    if (Utilities.IsNullOrUndefined(r?.Halt) || r.Halt === false)
                    {
                        navigationFunction();
                        self.scrollViewToTop();
                    }
                });
        }
        else
        {
            navigationFunction();
            this.scrollViewToTop();
        }
    }

    /**
     * ! isStepper
     */
    SkipToAction(): void
    {
        this.selectedIndex = this.getViews()
            .indexOf(this.getNextRequiredStep());
    }

    selecting: boolean = false;

    scrollViewToTop(): void
    {
        const view = document.querySelector(".metadata-form--card forge-scaffold [slot='body']");
        view.scrollTop = 0;
    }

    private navigateBack(cancelled: boolean, saved: boolean): void
    {
        if (this.isInitialForm)
        {
            this.return(cancelled, this.newForm);
            return;
        }

        if (this.currentDataController.$ParentFormData && this.currentDataController.$ParentFormData["$CopySourceId"])
            this.currentDataController.$ParentFormData["$CopySourceId"] = null;

        this.initialViewSet = false;
        this.formSaved = false;
        let formCtrlDirty = this.formCtrl.dirty;
        let leavingState = this.$metadataFormService.navigateBack(this.formStack, cancelled);

        if (this.$metadataViewService.IsSearchForm(this.currentForm))
            this.registerSearchBreadCrumbListener();

        const viewToUpdate = this.$metadataFormService.getViewToUpdate(this.formStack.current.selectedView, leavingState);

        let queryParams: Params;
        if (this.$metadataViewService.IsProjectionGrid(viewToUpdate) && this.queryParams.get("formType"))
        {
            queryParams = {
                "view": null,
                "formType": null,
                "editFormId": null,
                "idProperty": null,
                "lazyLoadFormType": null,
                "lazyLoadFormFilter": null
            };
        }
        else if (this.queryParams.get("view"))
        {
            queryParams = {
                "view": null
            };
        }
        else if (this.currentForm["$BreadcrumbFilter"])
        {
            queryParams = {};
        }

        if (queryParams)
        {
            if (this.currentForm["$BreadcrumbFilter"])
                queryParams["$filter"] = this.currentForm["$BreadcrumbFilter"];
            this.updateQueryParams(queryParams, true);
            delete this.currentForm["$BreadcrumbFilter"];
        }

        this.updateDirtyStateOnLeave(saved, formCtrlDirty, leavingState.parentIsDirty);
        this.viewOnly = this.currentState.formMetadata.ViewOnly;
    }

    private return(canceled: boolean, newForm: boolean): unknown
    {
        if (this.returnBindingIsSet)
        {
            this.onReturn.emit({
                cancelled: canceled,
                dataController: this.currentDataController,
                newForm: newForm,
            });
        }
        else if (
            this.$metadataViewService.IsCustomSaveForm(this.form) &&
            !!this.form.CustomPostSaveAction &&
            !canceled
        )
            return this.$metadataActionService.ExecuteAction(
                "Entity" in this.currentDataController
                    ? this.currentDataController["Entity"]
                    : this.currentDataController,
                this.form.CustomPostSaveAction,
                this.currentDataController
            );
        else if (this.$metadataViewService.IsCustomCancelForm(this.form))
        {
            this.$metadataActionService.ExecuteAction(
                "Entity" in this.currentDataController
                    ? this.currentDataController["Entity"]
                    : this.currentDataController,
                this.form.CancelAction,
                this.currentDataController
            ).then(x => this.closeOrNavigate(canceled, newForm));
        }
        //Only close or navigate back to main screen if cancel or standard save
        else
        {
            this.closeOrNavigate(canceled, newForm);
        }
    }

    get isEmbedded(): boolean
    {
        return this.$shellService.isEmbedded();
    }

    get isEmbeddedByRequest(): boolean
    {
        return StringHelper.parseBoolean(this.queryParams.get("isEmbedded"));
    }

    get isProvisional(): boolean
    {
        return this.$securityService.isProvisional;
    }

    closeOrNavigate(canceled: boolean, newForm: boolean)
    {
        if (this.$metadataActionService.delayTabClose)
            this.delayCloseOrNavigate(canceled, newForm);
        else
            this.closeOrNavigateImpl(canceled, newForm);
    }

    delayCloseOrNavigate(canceled: boolean, newForm: boolean)
    {
        const self = this;
        let interval = setInterval(() =>
        {
            if (self.$storageService.Get<string>(MetadataActionService.DocViewerLoaded) === "true")
            {
                self.$storageService.Remove(MetadataActionService.DocViewerLoaded);
                clearInterval(interval);
                self.$metadataActionService.delayTabClose = false;
                self.closeOrNavigateImpl(canceled, newForm);
            }
        }, 1000);
    }

    closeOrNavigateImpl(canceled: boolean, newForm: boolean)
    {
        if (this.closeOnFinish)
        {
            setTimeout(() => window.close(), 500, false);
            return;
        }

        // This is basically redundant with the below but it needs to be patched back and I don't want to risk breaking something
        // v10 core doesn't have 'window.opener' because of how Chromium works. Maybe need to rethink how we're doing communication between tabs for core.
        if (!canceled && this.passback)
        {
            this.$storageService.Set("passback", this.getModelValue(this.currentModel, this.passback));
        }

        if (this.$shellService.isEmbedded() && (newForm || this.navigateBackOnReturn))
        {
            if (!canceled)
                this.$storageService.Set(MetadataActionService.WindowSave, "true");
        }

        if (window.opener && (newForm || this.navigateBackOnReturn) && !this.isCefSharpClosing())
        {
            if (!canceled)
                this.$storageService.Set(MetadataActionService.WindowSave, "true");
            setTimeout(() => window.close(), 500, false);
            return;
        }

        else if (this.navigateBackOnReturn)
        {
            if (this.navigateBackUrl)
                window.location.href = this.navigateBackUrl;
            else
                window.history.go(-1);
        }
        else if (window.location.pathname.indexOf("/apps/form/") > -1 || window.location.pathname.indexOf("/apps/spatialForm/") > -1)
        {
            this.$navMenuService.toggle(true);
            if (this.$shellService.isEmbedded() && !this.returnBindingIsSet)
            {
                this.$shellService.closeSelfFromShell(this.$intentService.intentToResolve);
                this.$intentService.intentToResolve = null;
            }
        }
        else if (this.IsProcessingStep && !!document.referrer && document.referrer.indexOf("app/processing/step") < 0)
        {
            if (document.referrer.indexOf("formType=") > -1 || document.referrer.indexOf("UserProfileFormMetadata") > -1)
                window.location.href = "/" + window.location.pathname.split("/")[1].replace(/[^a-zA-Z0-9_-]/g, "") + "/apps/processing";
            else
                window.location.href = document.referrer;
            return;
        }
        else if (canceled && window.location.pathname.indexOf("/apps/processing") > -1 && this.queryParams.get("formType"))
        {
            //this.$locationService.path("/").search({});
            this.router.navigate(["/"], { queryParams: {} });
            requestAnimationFrame(() => window.location.reload());
            return;
        }
        this.$navigationService.triggerReset();
        this.router.navigate(["/"], { queryParams: {} });
        //this.$locationService.path("/").search({});
    }

    isCefSharpClosing(): boolean
    {
        return window.opener?.CefSharp && window.opener.CefSharp["Closing"] === true;
    }

    private _onDeleteMethod: onDeleteBoundMethodSignature;

    onDeleteBind()
    {
        if (!this._onDeleteMethod)
        {
            let self = this;
            this._onDeleteMethod = (t: onDeleteBoundMethodParameter) => self.onDelete(t.typename, t.model);
        }
        return this._onDeleteMethod;
    }

    onDelete(typename: string, model: any): Promise<any>
    {
        // REVIEW If we are on a child form should we not delete just leave them in the bag to be deleted on save?
        const cantSave =
            !Utilities.IsNullOrUndefined(this.selectedView.CantSave) && this.selectedView.CantSave === true;
        const immediateDelete = this.$metadataViewService.IsCombinedView(this.selectedView) ?
            this.$metadataViewService.AllBaseLevelViewsFromArray(this.selectedView.Views).some(view => view.ImmediateDelete) :
            this.selectedView.ImmediateDelete;
        if (cantSave || immediateDelete === true)
        {
            return this.doOnDelete(typename, model);
        }
        // Repect ImmediateDelete = false (not null) setting on the view
        if (immediateDelete === false || !this.isInitialForm || this.isInitialFormStepper || !this.hideActions)
        {
            return Promise.resolve();
        }
        return this.doOnDelete(typename, model);
    }

    doOnDelete(typename: string, model: any): Promise<any>
    {
        const self = this;
        model["ConfirmationQuestions"] = this.currentDataController.ConfirmationQuestions;
        const rejectionHandler = this.$metadataActionService.GetVerificationHandler(
            this.currentDataController,
            () => self.doOnDelete(typename, model),
            model
        );
        return this.$repositoryService
            .Save({
                Item1: typename,
                Item2: Utilities.stringify(model),
            })
            .then(processSaveResults, rejectionHandler)
            .catch(e =>
            {
                showError(e.data);
            })
            .finally(() =>
            {
                self.$gridDeleteService.triggerDeleteRetry();
            });
        function processSaveResults(results: any): void
        {
            self.currentDataController.ConfirmationQuestions = {};
            if (results && ObjectHelpers.isAfterSaveContext(results))
            {
                if (results.Halt) model.SerializedRowState = "";
                self.$metadataActionService.ProcessAfterSave(results, model, self.currentDataController);
            }
            if (
                Utilities.IsNullOrUndefined(self.selectedView.ImmediateDelete) ||
                self.selectedView.ImmediateDelete !== true
            )
            {
                self.$repositoryService.ClearAll();
            }
        }
        function showError(errorInfo: ErrorInfo): Promise<void>
        {
            model.SerializedRowState = "Unchanged";
            self.$metadataService.FireGridReload(typename, false);
            return self.$commonDialogsService.showAlertHtmlMessage(errorInfo.Message, "Acknowledge");
        }
    }

    cancel(): void
    {
        this.isDirtyPrompt(true).then(x =>
        {
            if (x)
                this.cancelImpl();
        });
    }

    cancelImpl(): void
    {
        if (this.formStack.current.addMode)
            this.$metadataInputSelectService.ClearOptions(this.currentForm, this.currentModel);
        this.canceling = true;
        this.$multipleEditEntityService.endEdit(this.formStack.current.addMode);
        this.navigateBack(true, false);
        this.projectionEdit = false;
        requestAnimationFrame(() => this.canceling = false);
    }

    canSave(): boolean
    {
        // if we are currently in the process of saving, lets stop the user from click save again
        if (
            (!this.allowSave() || !this.isViewOnly) &&
            !this.saving &&
            !this.recalculating &&
            !this.$metadataViewService.IsViewCellEditing(this.selectedView)
        )
            return true;
    }

    maybeWaitForCompoundRecalculate() :void
    {
        const action = this.getActionOverrides().filter(x => x.Label === this.currentCompoundSaveOption)[0];
        this.maybeWaitForRecalculate(action);
    }

    maybeWaitForRecalculate(overrideAction?: IMetadataAction): void
    {
        const self = this;
        requestAnimationFrame(() =>
        {
            if (this.isModelRecalculating(self.currentModel))
            {
                self.currentModel.$saveAfterRecalculate = true;
                self.currentModel.$saveAfterRecalculateAction = overrideAction;
            }
        });
    }

    isModelRecalculating(model: any): boolean
    {
        if (!model)
            return false;
        if (model.$recalculating)
            return true;
        for (const field in model)
        {
            if (Utilities.isObject(model[field]) && !field.startsWith("$") && this.isModelRecalculating(model[field]))
                return true;
        }
        return false;
    }

    _canCopy: boolean;
    get canCopy(): boolean
    {
        return this.$multipleEditEntityService.hasAddAndCopyListener();
    }

    save(overrideAction?: IMetadataAction): void
    {
        const self = this;
        requestAnimationFrame(() =>
        {
            if (self.currentModel?.$saveAfterRecalculate)
            {
                return;
            }

            if (!overrideAction && this.formStack.length > 1 && this.currentForm["$ChildFormSaveOverride"])
                overrideAction = this.currentForm["$ChildFormSaveOverride"];

            self.doSave(overrideAction);
        });
    }

    doSave(overrideAction?: IMetadataAction): void
    {
        if (this.resaving)
            return;
        const self = this;
        if (this.isViewOnly)
        {
            doAfterSave({cancel: true}, false);
            this.$metadataIframeService.MaybeResponseToParentWindow(true);
            return;
        }
        if (this.formCtrl.invalid)
        {
            this.$metadataService.touchForm(this.formCtrl.controls);
            const errors = this.$metadataViewService.FlattenFormErrors(this.currentForm.Views);
            if (!Utilities.IsNullOrUndefinedOrEmpty(errors))
            {
                let listItems: string[] = [];
                errors.forEach(error => listItems.push(`<li>${error}</li>`));
                self.$commonDialogsService.showAlertHtmlMessage(`<ul>${listItems.join("")}</ul>`, "Verification Errors");
                self.$metadataIframeService.MaybeResponseToParentWindow(false);
                return;
            }
        }
        const unloadedNeededViews = this.$metadataViewService.AllBaseLevelViews(this.formStack.form).filter(v => v.RequireLoadBeforeSave && !v["$HasBeenLoaded"]);
        if (unloadedNeededViews.length > 0)
        {
            this.resaving = true;
            this.$metadataLoadViewService.LoadView(this.formStack.model, unloadedNeededViews[0], this.formStack.dataCollection).then(a =>
            {
                this.resaving = false;
                this.doSave(overrideAction);
                return a;
            });
            this.$metadataIframeService.MaybeResponseToParentWindow(false);
            return;
        }
        let allowSave = self.allowSave();
        this.internalSave(overrideAction)
            .then(afterSave =>
            {
                doAfterSave(afterSave, allowSave);
            })
            .finally(() =>
            {
                self.viewOnly = self.currentState?.formMetadata.ViewOnly;
                if (self.isInitialForm || self.isInitialFormStepper)
                {
                    setTimeout(() => (self.saving = false), 500, false);
                }
                else
                {
                    self.saving = false;
                }

                self.$metadataIframeService.MaybeResponseToParentWindow(self.formSaved);
            });

        function doAfterSave(afterSave: AfterMetadataFormSave, saved: boolean)
        {
            self.formSaved = true;
            if (self.navigateEvent)
            {
                self.navigateNext();
            }
            else if ((self.formStack.AddNew || self.formStack.AddNewAndCopy) && !Utilities.IsNullOrUndefined(afterSave) && (!afterSave.cancel || self.isViewOnly) && (self.$multipleEditEntityService.hasAddNewListener() || self.SeparateEditForm))
            {
                if (self.formStack.AddNew)
                {
                    self.navigateNew(saved);
                }
                else if (self.formStack.AddNewAndCopy)
                {
                    self.navigateNew(saved, true);
                }
            }
            else if (!Utilities.IsNullOrUndefined(afterSave) && !afterSave.halt)
            {
                self.$multipleEditEntityService.endEdit(self.formStack.current.addMode);
                if (
                    Utilities.IsNullOrUndefined(overrideAction) ||
                    !MetadataActionHelpers.isReturnPathBack(overrideAction)
                )
                    self.navigateBack(afterSave?.cancel, saved);
            }
            else
            {
                self.formSaved = false;
                self.$multipleEditEntityService.endEdit(self.formStack.current.addMode);
                self.currentDataController.Entity = self.datacontroller.Entity;
                Utilities.mergeEntityCollections(
                    self.currentDataController.OtherEntities,
                    self.datacontroller.OtherEntities,
                    false
                );
                self.currentDataController.InitializeFormData();
                self.$metadataService.FireGridReload(self.currentDataController.Type, true);
            }
        }

    }

    private internalSave(overrideAction?: IMetadataAction): Promise<AfterMetadataFormSave>
    {
        const self = this;
        let savingPromise: Promise<AfterMetadataFormSave>;
        this.saving = true;
        if (self.allowSave())
        {
            // Todo - can we do away with this binding field and just work on the dataController passed into the component?
            this.currentDataController.ReturnDataOnSave =
                (Utilities.IsNullOrUndefined(this.formStack.ReturnDataOnSave) && this.returnDataOnSave) ||
                this.formStack.ReturnDataOnSave;
            savingPromise = this.$metadataFormService.save(this.formStack, overrideAction);
        }
        else
        {
            savingPromise = this.$metadataFormService
                .verify(this.formStack, !this.allowSave())
                .then(result => result);
        }
        return savingPromise;
    }

    private allowSave(): boolean
    {
        const parentForm = this.formStack.previousState;
        /**
        Form either has binding or IFormMetadata property set to ViewOnly
        */
        if (this.isViewOnly) return false;
        /**
        Top-level form OR
        the view calling this form is explicitly requiring this form to save OR
        form save handling is defined as immediate OR
        parent form explicitly overrides to CantSave
        */
        if (
            this.isInitialForm ||
            this.directSave ||
            this.currentForm.SaveHandling === SaveHandlingType.Immediate ||
            (parentForm && parentForm.formMetadata.Views[0].CantSave)
        )
            return true;
        /**
        Form save handling is defined as delay OR
        top-level form is a stepper OR
        parent form is a combined view(?)
        */
        if (
            this.currentForm.SaveHandling === SaveHandlingType.Delay ||
            this.isInitialFormStepper ||
            this.$metadataViewService.IsCombinedView(parentForm.formMetadata.Views[0])
        )
            return false;
        //default behavior for most views. save this form if it is the first child
        if (this.formStack.length > 1 && this.queryParams.get("lazyLoadFormType"))
            return false;
        return this.formStack.length === 2;
    }

    get hasChanges(): boolean
    {
        if (Utilities.IsNullOrUndefined(this.formCtrl)) return false;
        return this.formCtrl.dirty || this.isStackDirty || this.currentDataController.HasChanged();
    }

    hasActionOverrides(): boolean
    {
        return this.$metadataFormService.HasCustomSaveActions(this.currentForm);
    }

    hasFooterView(): boolean
    {
        return this.$metadataFormService.HasFooterView(this.currentForm);
    }

    hideCancelWithActionOverrides(): boolean
    {
        return this.$metadataFormService.HasCustomSaveActions(this.currentForm) &&
            !this.$metadataFormService.hasCustomCancelAction(this.form)
            ? this.currentForm.RemoveCancelButton
            : false;
    }

    getActionOverrides(): IMetadataAction[]
    {
        return this.$metadataFormService.HasCustomSaveActions(this.currentForm)
            ? this.currentForm.OverrideSaveActions
            : undefined;
    }

    IsActionHidden(action: IMetadataAction): boolean
    {
        return this.$metadataActionService.IsHidden(action, this.currentModel);
    }

    getOverrideLabel(overrideAction?: IMetadataAction): string
    {
        return this.isViewOnly ? overrideAction.Label.replace(/Save/g, "Done") : overrideAction.Label;
    }

    get showCompoundSaveButton(): boolean
    {
        return this.currentForm["$CompoundSaveAction"] ||
            (this.$metadataFormService.HasCustomSaveActions(this.currentForm)
            && this.currentForm.UseCompoundSaveButton);
    }

    currentCompoundSaveOption: string;

    private _compoundSaveOptions: IMenuOption[];
    get compoundSaveOptions(): IMenuOption[]
    {
        if (!this._compoundSaveOptions)
        {
            if (this.getActionOverrides()?.length === 0)
                return [];
            if (!this.currentCompoundSaveOption)
            {
                const primary = this.getActionOverrides().filter(x => x.Primary);
                this.currentCompoundSaveOption = primary.length > 0 ? primary[0].Label : this.getActionOverrides()[0].Label;
            }

            const options: IMenuOption[] = [];
            this.getActionOverrides().forEach(a =>
            {
                if (a.Label !== this.currentCompoundSaveOption)
                {
                    options.push(<IMenuOption>{
                        value: a.Label,
                        label: a.Label
                    });
                }
            });
            this._compoundSaveOptions = options;
        }
        return this._compoundSaveOptions;
    }

    saveCompound(option?: any): void
    {
        if (!!option)
        {
            this.currentCompoundSaveOption = option.detail.value;
            this._compoundSaveOptions = null;
        }
        const action = this.getActionOverrides().filter(x => x.Label === this.currentCompoundSaveOption)[0];
        this.save(action);
    }

    get multipleEdit(): boolean
    {
        return !this.formStack.current.addMode && !Utilities.IsNullOrUndefinedOrZero(this.multipleEditTotal);
    }

    get multipleEditTotal(): number
    {
        return this.$multipleEditEntityService.MultipleEditTotal;
    }

    get editFormIndex(): number
    {
        return this.$multipleEditEntityService.CurrentEditIndex;
    }

    leavingView: IViewMetadata;
    paginatorChange(e: CustomEvent): void
    {
        const self = this;

        e.preventDefault();

        requestAnimationFrame(() =>
        {
            if (self.isModelRecalculating(self.currentModel))
                self.currentModel.$paginatorChangeEvent = e;
            else
                self.paginatorChangeImpl(e);
        });
    }

    paginatorChangeImpl(e: CustomEvent): void
    {
        e.preventDefault();
        const self = this;
        this.leavingView = this.currentState.selectedView;
        this.navigateEvent = e.detail;
        if (this.allowSave() && this.hasChanges && !this.formSaved && !this.hasActionOverrides())
            this.$commonDialogsService
                .showConfirmationHtmlMessage("Do you want to save changes for this record?", "Save Changes?")
                .then(
                    () => self.save(),
                    () => self.navigateNext(false, false)
                );
        else this.navigateNext();
    }

    navigateNext(saved: boolean = true, checkDelay: boolean = true): void
    {
        this.navigateImpl(true, saved, false, checkDelay);
    }

    navigateNew(saved: boolean = false, copy: boolean = false): void
    {
        this.navigateImpl(false, saved, copy);
    }

    navigateImpl(edit: boolean, saved: boolean = true, copy: boolean = false, checkDelay: boolean = true): void
    {
        this.$multipleEditEntityService.IsEditNext = edit;
        let formCtrlDirty = this.formCtrl.dirty;
        const leavingState = this.formStack.pop();
        this.$providerStorageService.ClearGridProviders(leavingState.formMetadata);
        this.updateDirtyStateOnLeave(saved, formCtrlDirty, leavingState.parentIsDirty);

        this.$metadataFormService.syncOtherEntities(this.formStack, leavingState, false);

        if (edit)
        {
            this.$multipleEditEntityService.MultipleEditLoading = true;
            let index = this.$multipleEditEntityService.CurrentEditIndex + 1;
            if (this.navigateEvent)
                index = this.navigateEvent.pageIndex;
            this.$multipleEditEntityService.CurrentEditIndex = index;
            this.$multipleEditEntityService.editFormSelectedViewPath = this.queryParams.get("view");
            this.navigateEvent = undefined;
        }
        else
        {
            if (copy || this.$multipleEditEntityService.CopyProperties)
            {
                this.$multipleEditEntityService.DataController = leavingState.datacontroller;
                if (copy)
                {
                    if (this.projectionEdit)
                        this.$multipleEditEntityService.CopyEntity = this.currentDataController["$$ProjectionGridEntity"];
                    else
                        this.$multipleEditEntityService.CopyEntity = leavingState.datacontroller.Entity;
                }
            }
            else
                this.$multipleEditEntityService.DataController = undefined;
            this.$multipleEditEntityService.CopyAll = copy;
        }

        if (this.projectionEdit)
            delete this.currentDataController["$$ProjectionGridEntity"];

        if (checkDelay && !leavingState.formMetadata["$separateEditForm"])
        {
            if (leavingState.formMetadata.RecalculateParentFormOnSave)
            {
                this.$metadataLoadViewService.recalculating[leavingState.formMetadata.BoundType] = true;
                this.$multipleEditEntityService.MultipleEditLoading = true;
                this.$multipleEditEntityService.DelayNavigate++;
            }
            this.checkOnShownRecalculates(this.selectedView);
        }
        if (this.$multipleEditEntityService.DelayNavigate === 0)
        {
            if (edit)
                this.$multipleEditEntityService.editNext();
            else
            {
                if (leavingState.formMetadata["$separateEditForm"])
                    this.formStack = new MetadataFormStack<AugmentedModelObject, RepositoryFormData>(this.form, null, null);
                this.$multipleEditEntityService.addNew(leavingState.formMetadata["$separateEditForm"]);
            }

        }
        this.formSaved = false;
    }

    updateDirtyStateOnLeave(saved: boolean, formCtrlDirty: boolean, parentIsDirty: boolean)
    {
        if (saved)
        {
            this.markPristine(this.formCtrl.controls);
            this.isStackDirty = parentIsDirty;
        }
        else
            this.isStackDirty = parentIsDirty || formCtrlDirty || this.isStackDirty;
    }

    markPristine(controls: { [key: string]: AbstractControl<any, any>; }): void
    {
        Object.keys(controls).forEach((control,) =>
        {
            if (controls[control] instanceof FormGroup)
                this.markPristine((<FormGroup>controls[control]).controls);
            else
                controls[control].markAsPristine();
        });
    }

    checkOnShownRecalculates(view: IViewMetadata)
    {
        if (view.OnShown)
        {
            this.$multipleEditEntityService.MultipleEditLoading = true;
            this.$multipleEditEntityService.DelayNavigate++;
        }

        if (this.$metadataViewService.IsCombinedView(view))
            view.Views.forEach(v => this.checkOnShownRecalculates(v));
    }

    get multipleEditLoading(): boolean
    {
        return this.$multipleEditEntityService.MultipleEditLoading;
    }

    get showAddAnother(): boolean
    {
        return this.$multipleEditEntityService.hasAddNewListener() || this.SeparateEditForm;
    }

    get saveButtonText(): string
    {
        if (this.recalculating || this.saving)
            return "";
        if (Utilities.IsNullOrUndefined(this.formStack.SaveButtonText))
            return this.saveLabel;
        return this.formStack.SaveButtonText;
    }

    set saveButtonText(value: string)
    {
        this.formStack.SaveButtonText = value;
    }

    get saveOptions(): IMenuOption[]
    {
        return this.formStack.SaveOptions;
    }

    set saveOptions(value: IMenuOption[])
    {
        this.formStack.SaveOptions = value;
    }

    saveAction(option: any): void
    {
        if (option !== false)
        {
            switch (option.detail.value)
            {
                case this.newText:
                    this.formStack.AddNew = true;
                    this.formStack.AddNewAndCopy = false;
                    break;
                case this.copyText:
                    this.formStack.AddNew = false;
                    if (this.canCopy)
                    {
                        this.formStack.AddNewAndCopy = true;
                    }
                    break;
                default:
                    this.formStack.AddNew = false;
                    this.formStack.AddNewAndCopy = false;
                    this.saveButtonText = this.saveLabel;
                    break;
            }
        }

        this.save();
    }

    get isNavForm(): boolean
    {
        return this.getViews()?.length > 1;
    }

    menuOpened(e): void
    {
        e.target.activateFirstOption();
    }
}
